import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, X, ArrowRight, Home } from "lucide-react";
import TopBar from "../components/layout/TopBar";
import { scoreColor } from "../lib/growth";
import useApi from "../hooks/useApi";
import { useBranch } from "../context/BranchContext";

function Funnel({ funnel }) {
  const max = funnel[0]?.value || 1;
  return (
    <div className="space-y-2">
      {funnel.map((f, i) => {
        const pct = Math.round((f.value / max) * 100);
        const leak = i > 0 ? funnel[i - 1].value - f.value : 0;
        return (
          <div key={f.label} className="flex items-center gap-3" data-testid={`funnel-${i}`}>
            <div className="w-32 shrink-0 text-sm text-ink-2 text-right">{f.label}</div>
            <div className="flex-1 h-8 rounded-lg overflow-hidden" style={{ background: "#F1F2EE" }}>
              <div className="h-full rounded-lg flex items-center px-3 text-white text-xs font-bold transition-all duration-700"
                style={{ width: `${pct}%`, background: "var(--grad-green)" }}>{f.value}</div>
            </div>
            <div className="w-16 shrink-0 text-xs font-semibold" style={{ color: leak > 0 ? "#D0526B" : "transparent" }}>{leak > 0 ? `−${leak}` : ""}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function PropertyMarketing() {
  const navigate = useNavigate();
  const { branchId } = useBranch();
  const { data } = useApi(`/growth/property-marketing?branch_id=${branchId}`, [branchId]);
  const [open, setOpen] = useState(null);
  if (!data) return <><TopBar /><div className="p-6 sm:p-8 max-w-[1300px]">
    <div className="mb-6 animate-rise">
      <h1 className="text-3xl font-extrabold tracking-tighter text-ink">Property Marketing</h1>
      <p className="text-ink-2 mt-1">How effectively you're marketing the properties you have available.</p>
    </div>
    <div className="h-64 rounded-xl animate-pulse" style={{ background: "#EDEEE9" }} />
  </div></>;

  return (
    <>
      <TopBar />
      <div className="p-6 sm:p-8 max-w-[1300px]">
        <div className="mb-6 animate-rise">
          <h1 className="text-3xl font-extrabold tracking-tighter text-ink">Property Marketing</h1>
          <p className="text-ink-2 mt-1">How effectively you're marketing the properties you have available.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4 card p-8 flex flex-col justify-center" data-testid="coverage-hero">
            <div className="label-caps">Marketing coverage</div>
            <div className="text-6xl font-extrabold tracking-tighter mt-2" style={{ color: "#16180F" }}>{data.marketing_coverage}%</div>
            <p className="text-ink-2 text-sm mt-3 leading-relaxed">of your available property marketing opportunities are currently being used.</p>
            <div className="mt-4 rounded-xl px-4 py-3 text-sm" style={{ background: "rgba(217,92,92,.08)", color: "#C0455F" }}>
              <span className="font-bold">{data.no_social} properties</span> haven't received any social exposure.
            </div>
          </div>
          <div className="lg:col-span-8 card p-6">
            <div className="label-caps mb-4">Where marketing is leaking</div>
            <Funnel funnel={data.funnel} />
          </div>
        </div>

        <h2 className="text-xl font-bold tracking-tight text-ink mt-8 mb-4">Property marketing scores</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {data.properties.map((p) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="card card-hover overflow-hidden" data-testid={`property-${p.id}`}>
              <div className="flex gap-4 p-4">
                <div className="w-20 h-20 rounded-xl overflow-hidden shrink-0 bg-cover bg-center" style={{ backgroundImage: `url(${p.image})` }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-ink truncate">{p.address}</div>
                      <div className="text-xs text-ink-3 truncate">{p.type} · {p.price}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xl font-extrabold tracking-tighter" style={{ color: scoreColor(p.marketing_score) }}>{p.marketing_score}</div>
                      <div className="text-[10px] text-ink-3">/ 100</div>
                    </div>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: "#EDEEE9" }}>
                    <div className="h-full rounded-full" style={{ width: `${p.marketing_score}%`, background: scoreColor(p.marketing_score) }} />
                  </div>
                </div>
              </div>
              <div className="px-4 pb-4">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                  {p.checks.map((c) => (
                    <span key={c.label} className="inline-flex items-center gap-1.5 text-xs" style={{ color: c.ok ? "#5F8213" : "#9A9C90" }}>
                      {c.ok ? <Check size={13} strokeWidth={2.5} /> : <X size={13} strokeWidth={2.5} style={{ color: "#C0455F" }} />}{c.label}
                    </span>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t" style={{ borderColor: "#EDEEE9" }}>
                  <span className="text-xs font-semibold" style={{ color: "#A9781F" }}>{p.opportunities} opportunities available</span>
                  <button className="text-sm font-semibold inline-flex items-center gap-1 hover:gap-1.5 transition-all text-ink" data-testid={`improve-${p.id}`} onClick={() => navigate("/app/social-week")}>
                    Improve this property <ArrowRight size={14} strokeWidth={2} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </>
  );
}
